import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { Slot } from '../../internals/Slot';
import { useClickOutside } from '../../internals/useClickOutside';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useId } from '../../internals/useId';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';

import { useNavbarContext } from './NavbarContext';

/* ─────────────────────────── ProfileMenu ────────────────────────────────── */

export interface NavbarProfileMenuProps {
  /**
   * Custom trigger element. Receives merged aria-*, onClick and ref via `Slot`.
   * Pass **one** React element child; it will be cloned with trigger props.
   */
  children: React.ReactNode;
  /** Content rendered inside the floating panel (ProfileMenuItems, dividers, etc.). */
  content: React.ReactNode;
  semanticId?: string;
}

const panelSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const panelSlideOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-8px); }
`;

const StyledProfileWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
`;

const StyledProfilePanel = styled.div`
  position: absolute;
  right: 0;
  top: calc(var(--ui-navbar-h, ${rem(67)}) - ${rem(3)});
  z-index: ${zIndex.navbarDropdown};
  background: var(--ui-surface, ${palette.White});
  border-radius: var(--ui-radius-lg, ${rem(11)});
  box-shadow: 0 ${rem(2)} ${rem(20)} var(--ui-shadow-default, rgba(109, 109, 109, 0.15));
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  width: ${rem(245)};
  max-width: calc(100vw - ${rem(32)});
  padding: ${rem(10)} ${rem(13)};

  &[data-state='open'] {
    animation: ${panelSlideIn} 200ms ease-out forwards;
  }
  &[data-state='closed'] {
    animation: ${panelSlideOut} 150ms ease-in forwards;
  }
`;

function getEnabledItems(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
    (el) => el.dataset.disabled !== 'true',
  );
}

const NavbarProfileMenu: React.FC<NavbarProfileMenuProps> = ({
  children,
  content,
  semanticId,
}) => {
  const { openDropdownId, setOpenDropdownId } = useNavbarContext();
  const profileId = useId('nav-profile');
  const menuId = useId('nav-profile-menu');
  const triggerId = useId('nav-profile-trigger');

  const isOpen = openDropdownId === profileId;
  const setOpen = React.useCallback(
    (open: boolean) => setOpenDropdownId(open ? profileId : null),
    [profileId, setOpenDropdownId],
  );

  const triggerRef = React.useRef<HTMLElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [highlightedIdx, setHighlightedIdx] = React.useState(-1);

  const semanticProps = getSemanticAttributes(semanticId);

  const close = React.useCallback(() => setOpen(false), [setOpen]);

  useClickOutside(panelRef, close, isOpen, { ignoredRefs: [wrapperRef] });
  useEscapeKey(() => {
    close();
    setHighlightedIdx(-1);
    triggerRef.current?.focus();
  }, isOpen);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const items = getEnabledItems(panelRef.current);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        setOpen(true);
        return;
      }
      setHighlightedIdx((prev) => (prev + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIdx((prev) => (prev - 1 + items.length) % items.length);
    } else if (event.key === 'Enter' && isOpen && highlightedIdx >= 0) {
      event.preventDefault();
      items[highlightedIdx]?.click();
    }
  };

  React.useEffect(() => {
    if (!isOpen) return;
    const items = getEnabledItems(panelRef.current);
    items.forEach((el, i) => {
      el.setAttribute('data-highlighted', i === highlightedIdx ? 'true' : 'false');
    });
  }, [highlightedIdx, isOpen]);

  React.useEffect(() => {
    if (!isOpen) setHighlightedIdx(-1);
  }, [isOpen]);

  return (
    <StyledProfileWrapper ref={wrapperRef}>
      <Slot
        ref={triggerRef as React.Ref<HTMLElement>}
        id={triggerId}
        role="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        tabIndex={0}
        onClick={() => setOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        {...semanticProps}
      >
        {children}
      </Slot>

      {isOpen && (
        <StyledProfilePanel
          ref={panelRef}
          id={menuId}
          role="menu"
          aria-labelledby={triggerId}
          tabIndex={-1}
          data-state="open"
        >
          {content}
        </StyledProfilePanel>
      )}
    </StyledProfileWrapper>
  );
};

NavbarProfileMenu.displayName = 'Navbar.ProfileMenu';

/* ─────────────────────────── ProfileMenuItem ────────────────────────────── */

export interface NavbarProfileMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  disabled?: boolean;
  /** Visual variant. */
  variant?: 'default' | 'danger';
  semanticId?: string;
}

const TRANSIENT_PM = ['$active', '$variant'] as const;
const shouldForwardPM = (prop: string | number): boolean =>
  !TRANSIENT_PM.includes(prop as (typeof TRANSIENT_PM)[number]);

const StyledProfileMenuItem = styled.div.withConfig({
  shouldForwardProp: shouldForwardPM,
})<{ $active: boolean; $variant: 'default' | 'danger' }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;

  > a,
  > button,
  > span {
    font-size: ${rem(14)};
    line-height: ${rem(18)};
    color: var(--ui-text, ${palette.Black});
    display: flex;
    align-items: center;
    gap: ${rem(10)};
    font-weight: 400;
    border-radius: var(--ui-radius-sm, ${rem(4)});
    padding: ${rem(10)} ${rem(14)};
    transition: background 0.2s linear, color 0.2s linear, font-weight 0.1s linear;
    cursor: pointer;
    width: 100%;
    text-decoration: none;
    background: none;
    border: none;
    font-family: inherit;

    .material-symbols-outlined {
      font-size: ${rem(18)};
    }
  }

  &:hover > a,
  &:hover > button,
  &:hover > span,
  &[data-highlighted='true'] > a,
  &[data-highlighted='true'] > button,
  &[data-highlighted='true'] > span {
    background: color-mix(in srgb, var(--ui-interactive, ${palette.Purple}) 10%, transparent);
    color: var(--ui-interactive, ${palette.Purple});
    font-weight: 500;
  }

  ${({ $active }) =>
    $active &&
    css`
      > a,
      > button,
      > span {
        color: var(--ui-interactive, ${palette.Purple});
        font-weight: 500;
      }
    `}

  ${({ $variant }) =>
    $variant === 'danger' &&
    css`
      > a,
      > button,
      > span {
        color: var(--ui-muted, ${palette.SpanishGray});
      }

      &:hover > a,
      &:hover > button,
      &:hover > span,
      &[data-highlighted='true'] > a,
      &[data-highlighted='true'] > button,
      &[data-highlighted='true'] > span {
        background: color-mix(in srgb, var(--ui-interactive, ${palette.Purple}) 10%, transparent);
        color: var(--ui-interactive, ${palette.Purple});
        font-weight: 500;
      }
    `}

  &[data-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const NavbarProfileMenuItem = React.forwardRef<HTMLDivElement, NavbarProfileMenuItemProps>(
  function NavbarProfileMenuItem(
    { active = false, disabled = false, variant = 'default', semanticId, children, onClick, ...rest },
    ref,
  ) {
    const itemId = useId('nav-profile-item');
    const { setOpenDropdownId } = useNavbarContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      onClick?.(e);
      setOpenDropdownId(null);
    };

    return (
      <StyledProfileMenuItem
        ref={ref}
        id={itemId}
        role="menuitem"
        tabIndex={-1}
        $active={active}
        $variant={variant}
        data-active={active || undefined}
        data-disabled={disabled || undefined}
        onClick={handleClick}
        {...semanticProps}
        {...rest}
      >
        {children}
      </StyledProfileMenuItem>
    );
  },
);

NavbarProfileMenuItem.displayName = 'Navbar.ProfileMenuItem';

/* ─────────────────────────── ProfileMenuDivider ─────────────────────────── */

const StyledDivider = styled.div`
  height: 1px;
  background: ${palette.gray[200]};
  margin: ${rem(4)} 0;
`;

const NavbarProfileMenuDivider = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function NavbarProfileMenuDivider(props, ref) {
    return <StyledDivider ref={ref} role="separator" {...props} />;
  },
);

NavbarProfileMenuDivider.displayName = 'Navbar.ProfileMenuDivider';

/* ─────────────────────────── Exports ───────────────────────────────────── */

export { NavbarProfileMenu, NavbarProfileMenuItem, NavbarProfileMenuDivider };
